import { Router } from 'express';
import { z } from 'zod';
import { requireAuth, type AuthenticatedRequest } from './auth';
import { pool } from './db';
import { autoAssignGuardiansForCandidate } from './autoGuardianSelection';
import { encryptSensitive, protectContactHash } from './privacy';

export const guardianContactRouter = Router();
export const MINIMUM_COMMON_CONTACTS = 5;

const contactSchema = z.object({
  digest: z.string().regex(/^[a-f0-9]{64}$/i),
  name: z.string().trim().min(1).max(160),
  phone: z.string().trim().min(3).max(40),
});

const contactSyncSchema = z.object({
  fingerprintVersion: z.literal(2),
  contacts: z.array(contactSchema).min(MINIMUM_COMMON_CONTACTS).max(5000),
});

guardianContactRouter.put('/guardian-invitations/:id/contacts', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const id = z.uuid().safeParse(request.params.id);
    const parsed = contactSyncSchema.safeParse(request.body);
    if (!id.success || !parsed.success || request.authUser!.role !== 'user') {
      response.status(400).json({ error: 'Invalid contact synchronization data' }); return;
    }
    const client = await pool.connect();
    try {
      await client.query('BEGIN');
      const match = await client.query<{
        candidate_user_id: string; accepted_by: string | null; status: string;
        expires_at: Date; candidate_synced_at: Date | null; guardian_synced_at: Date | null;
      }>(`SELECT candidate_user_id,accepted_by,status,expires_at,candidate_synced_at,guardian_synced_at
          FROM guardian_invitations WHERE id=$1 FOR UPDATE`, [id.data]);
      const invitation = match.rows[0];
      const actorId = request.authUser!.id;
      const isCandidate = invitation?.candidate_user_id === actorId;
      const isGuardian = invitation?.accepted_by === actorId;
      if (!invitation || (!isCandidate && !isGuardian)) {
        await client.query('ROLLBACK'); response.status(404).json({ error: 'Guardian invitation not found' }); return;
      }
      if (!['pending', 'registered'].includes(invitation.status)
        || (isGuardian && invitation.status !== 'registered')
        || invitation.expires_at.getTime() <= Date.now()) {
        await client.query('ROLLBACK'); response.status(409).json({ error: 'Contact verification is closed or expired' }); return;
      }

      const uniqueContacts = new Map(parsed.data.contacts.map((contact) => [
        protectContactHash(contact.digest),
        { name: encryptSensitive(contact.name), phone: encryptSensitive(contact.phone) },
      ]));
      await client.query('DELETE FROM guardian_contact_hashes WHERE invitation_id=$1 AND user_id=$2',
        [id.data, actorId]);
      const contactRows = [...uniqueContacts.entries()];
      await client.query(
        `INSERT INTO guardian_contact_hashes
           (invitation_id,user_id,contact_digest,contact_name_encrypted,contact_phone_encrypted)
         SELECT $1,$2,digest,name,phone
         FROM UNNEST($3::text[],$4::text[],$5::text[]) AS contacts(digest,name,phone)`,
        [id.data, actorId, contactRows.map(([digest]) => digest),
          contactRows.map(([, contact]) => contact.name),
          contactRows.map(([, contact]) => contact.phone)],
      );
      const updated = await client.query<{ candidate_synced_at: Date | null; guardian_synced_at: Date | null }>(
        `UPDATE guardian_invitations SET
           candidate_synced_at=CASE WHEN candidate_user_id=$2 THEN NOW() ELSE candidate_synced_at END,
           guardian_synced_at=CASE WHEN accepted_by=$2 THEN NOW() ELSE guardian_synced_at END
         WHERE id=$1 RETURNING candidate_synced_at,guardian_synced_at`, [id.data, actorId],
      );

      let status = invitation.status;
      let commonContactCount: number | null = null;
      if (updated.rows[0].candidate_synced_at && updated.rows[0].guardian_synced_at) {
        const common = await client.query<{ count: number }>(
          `SELECT COUNT(*)::int AS count FROM (
             SELECT contact_digest FROM guardian_contact_hashes WHERE invitation_id=$1
             GROUP BY contact_digest HAVING COUNT(DISTINCT user_id)=2
           ) matches`, [id.data],
        );
        commonContactCount = common.rows[0].count;
        if (commonContactCount >= MINIMUM_COMMON_CONTACTS) {
          const evidence = await client.query<{
            contact_digest: string;
            candidate_name: string; candidate_phone: string;
            guardian_name: string; guardian_phone: string;
          }>(
            `SELECT contact_digest,
               MAX(contact_name_encrypted) FILTER (WHERE user_id=$2) AS candidate_name,
               MAX(contact_phone_encrypted) FILTER (WHERE user_id=$2) AS candidate_phone,
               MAX(contact_name_encrypted) FILTER (WHERE user_id=$3) AS guardian_name,
               MAX(contact_phone_encrypted) FILTER (WHERE user_id=$3) AS guardian_phone
             FROM guardian_contact_hashes WHERE invitation_id=$1
             GROUP BY contact_digest HAVING COUNT(DISTINCT user_id)=2
             ORDER BY contact_digest LIMIT $4`,
            [id.data, invitation.candidate_user_id, invitation.accepted_by, MINIMUM_COMMON_CONTACTS],
          );
          await client.query(
            `INSERT INTO guardian_contact_matches
               (invitation_id,contact_digest,candidate_name_encrypted,candidate_phone_encrypted,
                guardian_name_encrypted,guardian_phone_encrypted)
             SELECT $1,digest,candidate_name,candidate_phone,guardian_name,guardian_phone
             FROM UNNEST($2::text[],$3::text[],$4::text[],$5::text[],$6::text[])
               AS matches(digest,candidate_name,candidate_phone,guardian_name,guardian_phone)
             ON CONFLICT (invitation_id,contact_digest) DO NOTHING`,
            [id.data, evidence.rows.map((item) => item.contact_digest),
              evidence.rows.map((item) => item.candidate_name),
              evidence.rows.map((item) => item.candidate_phone),
              evidence.rows.map((item) => item.guardian_name),
              evidence.rows.map((item) => item.guardian_phone)],
          );
          status = 'verified';
        }
        await client.query(
          `UPDATE guardian_invitations SET common_contact_count=$2,status=$3,
             completed_at=CASE WHEN $3='verified' THEN NOW() ELSE completed_at END
           WHERE id=$1`,
          [id.data, commonContactCount, status],
        );
        await client.query('DELETE FROM guardian_contact_hashes WHERE invitation_id=$1', [id.data]);
      }
      await client.query(
        "INSERT INTO audit_events (user_id,event_type,details) VALUES ($1,'guardian_contacts_synchronized',$2::jsonb)",
        [actorId, JSON.stringify({ invitationId: id.data, submittedCount: uniqueContacts.size,
          commonContactCount, result: status })],
      );
      await client.query('COMMIT');
      if (status === 'verified') await autoAssignGuardiansForCandidate(invitation.candidate_user_id);
      response.json({ status, commonContactCount, minimumCommonContacts: MINIMUM_COMMON_CONTACTS });
    } catch (error) {
      await client.query('ROLLBACK'); throw error;
    } finally { client.release(); }
  });
